import {
  buildFetchOptionsWithAuth,
  fetchJson,
  type MutateOptions,
  showApiError,
  useMutation,
  useQueryClient,
} from '@fv/client-core'
import type { Quote } from '@fv/client-types'
import type { ChargeName, Currency } from '@fv/models'
import type { EquipmentType } from '@fv/models/equipment'

import { apiUri } from '@/constants'
import { quoteKeys } from '@/hooks/shipments/useQuotes'

import { buildSpotQuoteCharges } from './spotQuoteCharges'
import {
  type ManualCarrierRate,
  type ManualRateType,
  type ManualSpotQuote,
  useManualRateForm,
} from './useManualRateForm'

export const newQuoteId = 'newQuote'

type BaseManualQuoteDTO = {
  amount: number
  carrierId: string
  carrierName: string
  currency: Currency
  loadId: string
  quoteNum: string
  type: ManualRateType
}

type ManualCarrierRateDTO = BaseManualQuoteDTO & {
  days?: number
  serviceType?: string
  serviceId?: string
  type: 'manual-rate'
}

type ManualSpotQuoteDTO = BaseManualQuoteDTO & {
  autoCreateCarrierName: string
  carrierEmail: string
  charges?: Array<{ name: ChargeName; amount: number }>
  equipmentType: EquipmentType
  type: 'spot-quote'
}

async function postQuoteToRequest(
  dto: ManualCarrierRateDTO | ManualSpotQuoteDTO,
): Promise<Quote> {
  const endpoint = `${apiUri}/shipments/${dto.loadId}/quotes`
  const options = buildFetchOptionsWithAuth({
    body: JSON.stringify(dto),
    method: 'POST',
  })

  const response = await fetchJson(endpoint, options)
  if (response.ok) return response.json
  throw response.errorMessage
}

type CreateQuoteOptions = MutateOptions<
  Quote,
  unknown,
  ManualCarrierRateDTO | ManualSpotQuoteDTO
>

type CreateQuoteValues = {
  loadId: string
  quote: ManualCarrierRate | ManualSpotQuote
}

export function useCreateQuote() {
  const queryClient = useQueryClient()
  const createQuoteMutation = useMutation(postQuoteToRequest, {
    onSettled(quote, error, dto) {
      queryClient.setQueryData<Quote[]>(
        quoteKeys.load(dto.loadId, quote?.loadQuoteVersion),
        prev => {
          const quotes = prev?.filter(
            q => q._id !== quote?._id && q._id !== newQuoteId,
          )

          if (!quote) return quotes
          return quotes?.concat(quote) || [quote]
        },
      )

      if (error) showApiError('Unable to add quote', error)
    },
  })

  function createQuote(
    values: CreateQuoteValues,
    options?: CreateQuoteOptions,
  ) {
    if (createQuoteMutation.isPending) return

    const { loadId, quote } = values
    const currency =
      quote.rateType === 'spot-quote'
        ? (quote as ManualSpotQuote).currency
        : 'usd'

    const baseDTO: BaseManualQuoteDTO = {
      amount: Number(quote.price),
      carrierId: quote.carrierId,
      carrierName: quote.carrierName.trim(),
      currency,
      loadId,
      quoteNum: quote.quoteNumber.trim(),
      type: quote.rateType,
    }

    if (baseDTO.type === 'manual-rate') {
      const { days, serviceType, serviceId } = quote as ManualCarrierRate
      const dto: ManualCarrierRateDTO = {
        ...baseDTO,
        ...(days && { days: Number(days) }),
        ...(serviceType.trim() && { serviceType: serviceType.trim() }),
        ...(serviceId.trim() && { serviceId: serviceId.trim() }),
        type: baseDTO.type,
      }

      return createQuoteMutation.mutate(dto, options)
    }

    if (baseDTO.type === 'spot-quote') {
      const { carrierEmail, equipmentType } = quote as ManualSpotQuote
      const load = useManualRateForm.getState().load
      const charges = load
        ? buildSpotQuoteCharges(currency, baseDTO.amount, load.locations)
        : undefined
      const dto: ManualSpotQuoteDTO = {
        ...baseDTO,
        ...(charges && {
          amount: charges.reduce((sum, c) => sum + c.amount, 0),
          charges,
        }),
        autoCreateCarrierName: quote.carrierName.trim(),
        carrierEmail: carrierEmail.trim(),
        equipmentType,
        type: baseDTO.type,
      }

      return createQuoteMutation.mutate(dto, options)
    }
  }

  return createQuote
}
